import React from 'react';
import { Helmet } from 'react-helmet-async';

const CGU: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#E8EEF8] text-gray-800">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 py-20">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Conditions Générales d'Utilisation
          </h1>
        </div>

        {/* Version */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <p className="font-semibold text-gray-800">Version en vigueur au : 21 mars 2026</p>
          <p className="mt-4 leading-relaxed text-gray-700">
            Les présentes Conditions Générales d'Utilisation régissent l'accès et l'utilisation du site{' '}
            <strong>www.totheroad.fr</strong>, édité par la société TO THE ROAD.
          </p>
        </div>

        {/* Informations légales */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Informations légales</h2>
          <div className="leading-relaxed text-gray-700 space-y-1">
            <p><strong>To The Road</strong></p>
            <p>Société par Actions Simplifiée (SAS)</p>
            <p>Capital social : 1 000 €</p>
            <p>Siège social : 61 rue de Lyon, 75012 Paris, France</p>
            <p>SIREN : 989 290 754 00013</p>
            <p>RCS : Paris 989 290 754</p>
            <p>Code APE/NAF : 7711A</p>
            <p>N° TVA intracommunautaire : FR37 989290754</p>
            <p>
              Email :{' '}
              <a href="mailto:contact@totheroad.fr" className="text-blue-500 font-semibold hover:underline">
                contact@totheroad.fr
              </a>
            </p>
            <p>Site web : www.totheroad.fr</p>
            <p className="mt-3 text-xs text-gray-300"><strong className="text-gray-300 font-normal">Directeur de la publication :</strong> Mathieu DESCHAMPS</p>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-10">

          {/* Préambule */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">Préambule</h3>
            <p className="text-gray-700 leading-relaxed">
              <strong>To The Road</strong> est une <strong>plateforme d'information et de comparaison</strong> permettant
              aux utilisateurs de découvrir des véhicules de loisirs et d'être redirigés vers des plateformes de location
              tierces pour effectuer leurs réservations.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3 font-semibold">
              To The Road ne procède à aucune réservation, ne collecte aucun paiement et n'intervient pas dans la
              relation contractuelle entre l'utilisateur et les plateformes de location tierces.
            </p>
          </div>

          {/* Article 1 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 1 - Objet et champ d'application</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">1.1 Objet</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Les présentes CGU ont pour objet de définir les conditions d'accès et d'utilisation du Site par tout
              visiteur (ci-après <strong>« l'Utilisateur »</strong>).
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              Le Site To The Road est une <strong>plateforme d'information</strong> qui :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2">
              <li>Présente la société To The Road et ses activités</li>
              <li>Affiche un catalogue de véhicules de loisirs disponibles à la location</li>
              <li>
                Fournit des liens de redirection vers des plateformes de location tierces (Getaround, Wikicampers,
                Yescapa, Roadstr, Turo, etc.)
              </li>
              <li>Permet aux utilisateurs de s'inscrire à une newsletter</li>
              <li>Met à disposition un formulaire de contact</li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">1.2 Acceptation des CGU</h3>
            <p className="text-gray-700 leading-relaxed mb-2">
              L'accès au Site implique l'acceptation pleine et entière des présentes CGU par l'Utilisateur.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Site.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">1.3 Modification des CGU</h3>
            <p className="text-gray-700 leading-relaxed mb-2">
              To The Road se réserve le droit de modifier les présentes CGU à tout moment. Les modifications entreront
              en vigueur dès leur publication sur le Site.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Il est conseillé à l'Utilisateur de consulter régulièrement les CGU afin de prendre connaissance des
              éventuelles modifications.
            </p>
          </section>

          {/* Article 2 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 2 - Fonctionnement du site</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">2.1 Service d'information et de redirection</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Le Site To The Road est <strong>exclusivement un outil d'information et de comparaison</strong>. Il permet
              à l'Utilisateur de :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2 mb-4">
              <li>Consulter un catalogue de véhicules de loisirs</li>
              <li>Comparer les offres disponibles sur différentes plateformes de location</li>
              <li>Être redirigé vers les plateformes de location tierces pour effectuer une réservation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold">
              To The Road n'intervient à aucun moment dans le processus de réservation, de paiement ou de location.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">2.2 Plateformes tierces partenaires</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Les véhicules présentés sur le Site sont disponibles à la location via des plateformes tierces, notamment :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2 mb-3">
              <li>Getaround</li>
              <li>Roadstr</li>
              <li>Wikicampers</li>
              <li>Yescapa</li>
              <li>Turo</li>
            </ul>
            <p className="text-gray-700 text-sm">Cette liste est non exhaustive et peut être modifiée à tout moment.</p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">2.3 Absence de responsabilité contractuelle</h3>
            <p className="text-gray-700 leading-relaxed mb-2 font-semibold">
              To The Road n'est pas partie prenante dans la relation contractuelle entre l'Utilisateur et les plateformes
              de location tierces.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Toute réservation, tout paiement et toute prestation de location sont gérés directement par les plateformes
              tierces, qui définissent leurs propres conditions générales, tarifs, assurances et conditions d'annulation.
            </p>
          </section>

          {/* Article 3 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 3 - Responsabilités</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">3.1 Responsabilité de To The Road</h3>
            <p className="text-gray-700 leading-relaxed mb-3">To The Road s'engage à :</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2">
              <li>Fournir des informations exactes et à jour sur les véhicules présentés</li>
              <li>Assurer le bon fonctionnement technique du Site dans la mesure du possible</li>
              <li>Protéger les données personnelles conformément au RGPD (voir Article 6)</li>
              <li>Répondre aux demandes d'information via le formulaire de contact</li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">3.2 Limitation de responsabilité</h3>
            <p className="text-gray-700 leading-relaxed mb-3 font-semibold">
              To The Road ne saurait être tenue responsable :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2">
              <li>De la disponibilité réelle des véhicules présentés</li>
              <li>
                Des conditions tarifaires, d'assurance, d'annulation ou de modification appliquées par les plateformes
                tierces
              </li>
              <li>De tout litige, dommage ou préjudice résultant d'une location effectuée via une plateforme tierce</li>
              <li>De l'exactitude ou de l'exhaustivité des informations fournies par les plateformes tierces</li>
              <li>Des interruptions, erreurs ou dysfonctionnements du Site indépendants de sa volonté</li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">3.3 Responsabilité des plateformes tierces</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Les plateformes de location tierces sont seules responsables de :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2">
              <li>La gestion des réservations et des paiements</li>
              <li>L'état, la conformité et la disponibilité des véhicules</li>
              <li>Les assurances applicables</li>
              <li>Le traitement des réclamations et litiges liés à la location</li>
              <li>L'application de leurs propres conditions générales</li>
            </ul>
          </section>

          {/* Article 4 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 4 - Obligations de l'utilisateur</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">4.1 Utilisation conforme</h3>
            <p className="text-gray-700 leading-relaxed">
              L'Utilisateur s'engage à utiliser le Site de manière conforme à sa destination et aux présentes CGU.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">4.2 Informations exactes</h3>
            <p className="text-gray-700 leading-relaxed">
              Lors de l'inscription à la newsletter ou de l'utilisation du formulaire de contact, l'Utilisateur s'engage
              à fournir des informations exactes, complètes et à jour.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">4.3 Interdictions</h3>
            <p className="text-gray-700 leading-relaxed mb-3">Il est strictement interdit de :</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2">
              <li>Utiliser le Site à des fins frauduleuses, illégales ou contraires à l'ordre public</li>
              <li>Tenter de nuire au bon fonctionnement du Site (piratage, virus, etc.)</li>
              <li>Extraire, copier ou réutiliser tout contenu du Site sans autorisation</li>
              <li>Usurper l'identité d'un tiers</li>
            </ul>
          </section>

          {/* Article 5 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 5 - Disponibilité du site</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">5.1 Accessibilité</h3>
            <p className="text-gray-700 leading-relaxed">
              To The Road s'efforce de rendre le Site accessible 24h/24 et 7j/7, sauf en cas de force majeure ou de
              maintenance.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">5.2 Interruptions</h3>
            <p className="text-gray-700 leading-relaxed">
              To The Road se réserve le droit de suspendre, interrompre ou limiter l'accès au Site à tout moment,
              notamment pour des raisons de maintenance, de mise à jour ou pour tout autre motif technique.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">5.3 Absence de garantie</h3>
            <p className="text-gray-700 leading-relaxed">
              To The Road ne garantit pas que le Site sera exempt d'erreurs, de bugs ou d'interruptions.
            </p>
          </section>

          {/* Article 6 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 6 - Protection des données personnelles</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">6.1 Politique de confidentialité</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To The Road collecte et traite des données personnelles dans le respect du Règlement Général sur la
              Protection des Données (RGPD) et de la loi Informatique et Libertés.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <p className="text-gray-700 text-sm font-semibold">
                Pour consulter les détails sur la collecte, le traitement, la conservation et vos droits concernant vos
                données personnelles, veuillez consulter notre{' '}
                <a href="/politique-confidentialite" className="text-blue-500 hover:underline">
                  Politique de Confidentialité
                </a>
                .
              </p>
            </div>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">6.2 Responsable du traitement</h3>
            <p className="text-gray-700 leading-relaxed mb-2">
              Le responsable du traitement des données personnelles est :
            </p>
            <div className="text-gray-700 text-sm leading-relaxed ml-2">
              <p className="font-semibold">To The Road</p>
              <p>61 rue de Lyon, 75012 Paris, France</p>
              <p>
                Email :{' '}
                <a href="mailto:contact@totheroad.fr" className="text-blue-500 hover:underline">
                  contact@totheroad.fr
                </a>
              </p>
            </div>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">6.3 Données collectées</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              To The Road collecte uniquement les données suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2">
              <li>
                <strong>Newsletter</strong> : Adresse email
              </li>
              <li>
                <strong>Formulaire de contact</strong> : Nom, prénom, adresse email, numéro de portable, message
              </li>
              <li>
                <strong>Cookies</strong> : Données de navigation (voir{' '}
                <a href="/cookies" className="text-blue-500 hover:underline">
                  Politique Cookies
                </a>
                )
              </li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">6.4 Vos droits RGPD</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm ml-2 mb-4">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement (« droit à l'oubli »)</li>
              <li>Droit d'opposition au traitement</li>
              <li>Droit à la limitation du traitement</li>
              <li>Droit à la portabilité de vos données</li>
            </ul>
            <p className="text-gray-700 text-sm font-semibold">
              Pour exercer ces droits, contactez-nous à :{' '}
              <a href="mailto:contact@totheroad.fr" className="text-blue-500 hover:underline font-semibold">
                contact@totheroad.fr
              </a>
            </p>
          </section>

          {/* Article 7 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 7 - Service client</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">7.1 Contact To The Road</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Pour toute question concernant le Site, la newsletter ou une demande d'information générale :
            </p>
            <div className="bg-gray-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <p className="text-gray-700 text-sm">
                <strong>Email :</strong>{' '}
                <a href="mailto:contact@totheroad.fr" className="text-blue-500 hover:underline">
                  contact@totheroad.fr
                </a>
              </p>
            </div>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">7.2 Questions sur les réservations</h3>
            <p className="text-gray-700 leading-relaxed mb-2">
              Pour toute question relative à une réservation, un paiement, une assurance ou une location de véhicule,
              veuillez contacter <strong>directement la plateforme de location</strong> concernée (Getaround, Yescapa,
              etc.).
            </p>
            <p className="text-gray-700 leading-relaxed">
              To The Road n'intervient pas dans la gestion des réservations.
            </p>
          </section>

          {/* Article 8 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 8 - Propriété intellectuelle</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">8.1 Droits de propriété</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              L'ensemble des éléments du Site (textes, images, logos, vidéos, design, code source, base de données,
              etc.) est la propriété exclusive de To The Road ou fait l'objet d'une autorisation d'utilisation.
            </p>
            <p className="text-gray-700 leading-relaxed">
              La marque <strong>« TO THE ROAD »</strong> ainsi que les logos sont des marques déposées.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">8.2 Utilisation interdite</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Toute reproduction, représentation, modification, publication, adaptation, transmission ou exploitation du
              Site ou de son contenu, par quelque procédé que ce soit, est interdite sans l'autorisation écrite
              préalable de To The Road.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Toute exploitation non autorisée constituerait une contrefaçon sanctionnée par les articles L.335-2 et
              suivants du Code de la Propriété Intellectuelle.
            </p>
          </section>

          {/* Article 9 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 9 - Droit applicable et juridiction</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">9.1 Droit français</h3>
            <p className="text-gray-700 leading-relaxed">
              Les présentes CGU sont régies par le droit français.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">9.2 Règlement des litiges</h3>
            <p className="text-gray-700 leading-relaxed mb-2">
              En cas de litige, les parties s'efforceront de trouver une solution amiable avant toute action judiciaire.
            </p>
            <p className="text-gray-700 leading-relaxed">
              À défaut d'accord amiable, tout litige sera porté devant les tribunaux français compétents selon les règles
              de droit commun.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">
              9.3 Plateforme européenne de règlement des litiges
            </h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Conformément à l'article 14 du Règlement (UE) n°524/2013, la Commission Européenne met à disposition une
              plateforme de Règlement en Ligne des Litiges (RLL) accessible au lien suivant :
            </p>
            <a
              href="https://ec.europa.eu/consumers/odr/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline text-sm"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
          </section>

          {/* Article 10 */}
          <section className="border-b border-[#E8EEF8] pb-10">
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 10 - Réclamations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pour toute réclamation concernant le Site, vous pouvez nous contacter :
            </p>
            <div className="bg-gray-50 border-l-4 border-blue-400 p-4 rounded-r-lg text-sm text-gray-700 space-y-1">
              <p>
                <strong>Email :</strong>{' '}
                <a href="mailto:contact@totheroad.fr" className="text-blue-500 hover:underline">
                  contact@totheroad.fr
                </a>
              </p>
              <p>
                <strong>Adresse postale :</strong> TO THE ROAD — 61 rue de Lyon, 75012 Paris, France
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mt-4">
              Nous nous engageons à traiter votre réclamation dans les meilleurs délais.
            </p>
          </section>

          {/* Article 11 */}
          <section>
            <h2 className="text-xl font-bold text-blue-500 mb-5">Article 11 - Dispositions diverses</h2>

            <h3 className="text-base font-semibold text-gray-800 mt-5 mb-3">11.1 Nullité partielle</h3>
            <p className="text-gray-700 leading-relaxed">
              Si une ou plusieurs dispositions des présentes CGU sont jugées nulles ou inapplicables, les autres
              dispositions resteront en vigueur.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">11.2 Renonciation</h3>
            <p className="text-gray-700 leading-relaxed">
              Le fait pour To The Road de ne pas se prévaloir d'une disposition des CGU ne constitue pas une renonciation
              à cette disposition.
            </p>

            <h3 className="text-base font-semibold text-gray-800 mt-6 mb-3">11.3 Intégralité</h3>
            <p className="text-gray-700 leading-relaxed">
              Les présentes CGU constituent l'intégralité de l'accord entre To The Road et l'Utilisateur concernant
              l'utilisation du Site.
            </p>
          </section>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t-2 border-[#E8EEF8] text-center">
            <p className="text-gray-400 text-sm italic">
              Dernière mise à jour : 21 mars 2026
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CGU;
